/* ==========================================================================
   CHLOIA — product page
   Gallery · phone selector · design upload · variants · quantity tiers · add to cart · pincode · sticky bar
   ========================================================================== */
(() => {
  if (customElements.get('ch-product')) return;

  const PHONE_KEY = 'chloia:phone';
  const PIN_KEY = 'chloia:pincode';
  const money = (cents) => (window.Chloia ? window.Chloia.money(cents) : String(cents / 100));
  const toast = (msg) => window.Chloia && window.Chloia.toast(msg);

  const formatSize = (bytes) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`);

  const TYPE_BY_EXT = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif' };

  const readPhone = () => {
    try {
      const v = JSON.parse(localStorage.getItem(PHONE_KEY) || 'null');
      return v && v.brand ? v : { brand: '', model: '' };
    } catch (e) {
      return { brand: '', model: '' };
    }
  };

  const writePhone = (phone) => {
    try {
      localStorage.setItem(PHONE_KEY, JSON.stringify(phone));
    } catch (e) {}
  };

  class ChProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-ch-product-json]');
      this.data = json ? JSON.parse(json.textContent) : { variants: [], media: [], tiers: {} };
      this.form = this.querySelector('[data-ch-product-form]');
      this.variant = this.currentVariant();
      this.qty = 1;
      this.state = 'idle';

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

      this.initGallery();
      this.initPhone();
      this.initUpload();
      this.initPin();
      this.initSticky();
      this.render();
    }

    /* ---------- Helpers ---------- */

    $(sel) {
      return this.querySelector(sel);
    }

    $$(sel) {
      return Array.from(this.querySelectorAll(sel));
    }

    currentVariant() {
      const input = this.$('[data-ch-variant-id]');
      const id = input ? Number(input.value) : null;
      return this.data.variants.find((v) => v.id === id) || this.data.variants[0];
    }

    get phoneStep() {
      return this.$('[data-ch-phone-step]');
    }

    get phoneRequired() {
      const step = this.phoneStep;
      return !!step && step.getAttribute('data-required') === 'true';
    }

    get model() {
      const select = this.$('[data-ch-model]');
      return select ? select.value : '';
    }

    get phoneReady() {
      return !this.phoneRequired || !!this.model;
    }

    get customStep() {
      return this.$('[data-ch-custom]');
    }

    get uploadInput() {
      return this.$('[data-ch-upload-input]');
    }

    get hasFile() {
      const input = this.uploadInput;
      return !!(input && input.files && input.files.length);
    }

    get designReady() {
      const step = this.customStep;
      return !step || step.getAttribute('data-required') !== 'true' || this.hasFile;
    }

    get ready() {
      return this.phoneReady && this.designReady;
    }

    shake(step) {
      if (!step) return;
      step.classList.remove('is-shake');
      void step.offsetWidth;
      step.classList.add('is-shake');
      if (window.innerWidth < 880) step.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    tierTotal(qty) {
      const t = this.data.tiers || {};
      const unit = t.unit || 349;
      if (!this.data.bundle) return (this.variant ? this.variant.price : 0) * qty;
      let base;
      if (qty <= 1) base = unit;
      else if (qty === t.q2) base = t.p2;
      else if (qty === t.q3) base = t.p3;
      else if (qty > t.q3) base = t.p3 + (qty - t.q3) * Math.round(t.p3 / t.q3);
      else base = unit * qty;
      const premium = (this.variant.price / 100 - unit) * qty;
      return Math.round((base + premium) * 100);
    }

    /* ---------- Gallery ---------- */

    initGallery() {
      this.slides = this.$$('[data-ch-slide]');
      this.thumbs = this.$$('[data-ch-thumb]');
      const tile = this.$('[data-ch-zoom]');
      const hint = this.$('[data-ch-zoom-hint]');
      if (tile && hint && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        tile.addEventListener('mouseenter', () => {
          tile.classList.add('is-zoomed');
          hint.textContent = hint.dataset.active;
        });
        tile.addEventListener('mouseleave', () => {
          tile.classList.remove('is-zoomed');
          hint.textContent = hint.dataset.idle;
        });
      }
      if (tile && this.slides.length > 1) {
        let x0 = null;
        tile.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
        tile.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          x0 = null;
          if (Math.abs(dx) < 40) return;
          const next = (this.activeIndex() + (dx < 0 ? 1 : -1) + this.slides.length) % this.slides.length;
          this.showMedia(next);
        });
      }
    }

    activeIndex() {
      return Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
    }

    showMedia(index) {
      if (!this.slides.length) return;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      const count = this.$('[data-ch-gallery-count]');
      if (count) {
        const n = String(index + 1).padStart(2, '0');
        count.textContent = `${n} / ${count.dataset.total}`;
      }
      const video = this.slides[index].querySelector('video');
      if (video) video.play().catch(() => {});
    }

    /* ---------- Phone ---------- */

    initPhone() {
      const brand = this.$('[data-ch-brand]');
      if (!brand) return;
      const saved = readPhone();
      if (saved.brand && [...brand.options].some((o) => o.value === saved.brand)) brand.value = saved.brand;
      this.fillModels(saved.brand === brand.value ? saved.model : '');
    }

    fillModels(selected) {
      const brand = this.$('[data-ch-brand]');
      const model = this.$('[data-ch-model]');
      const tpl = this.$('template[data-ch-models]');
      if (!brand || !model || !tpl) return;
      const placeholder = model.options[0] ? model.options[0].outerHTML : '<option value=""></option>';
      const group = [...tpl.content.querySelectorAll('optgroup')].find((g) => g.dataset.brand === brand.value);
      model.innerHTML = placeholder + (group ? group.innerHTML : '');
      if (selected && [...model.options].some((o) => o.value === selected)) model.value = selected;
      else model.value = '';
    }

    savePhone() {
      const brand = this.$('[data-ch-brand]');
      if (!brand) return;
      writePhone({ brand: brand.value, model: this.model });
    }

    /* ---------- Design upload ---------- */

    initUpload() {
      const step = this.customStep;
      const input = this.uploadInput;
      if (!step || !input) return;
      input.addEventListener('change', () => this.onFile());

      const zone = step.querySelector('[data-ch-upload-zone]');
      if (!zone) return;
      ['dragenter', 'dragover'].forEach((type) =>
        zone.addEventListener(type, (e) => {
          e.preventDefault();
          zone.classList.add('is-drag');
        })
      );
      ['dragleave', 'dragend', 'drop'].forEach((type) => zone.addEventListener(type, () => zone.classList.remove('is-drag')));
      zone.addEventListener('drop', (e) => {
        e.preventDefault();
        const files = e.dataTransfer && e.dataTransfer.files;
        if (!files || !files.length) return;
        try {
          const dt = new DataTransfer();
          dt.items.add(files[0]);
          input.files = dt.files;
        } catch (err) {
          input.files = files;
        }
        this.onFile();
      });
    }

    onFile() {
      const step = this.customStep;
      const input = this.uploadInput;
      const error = step.querySelector('[data-ch-upload-error]');
      const file = input.files && input.files[0];
      if (error) error.hidden = true;

      if (file) {
        const maxMb = parseFloat(step.dataset.maxMb) || 10;
        const accepted = (step.dataset.accept || '').split(',').map((t) => t.trim().toLowerCase());
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const type = (file.type || TYPE_BY_EXT[ext] || '').toLowerCase();
        let message = '';
        if (!accepted.includes(type) && !accepted.includes(`.${ext}`)) {
          message = `Use a ${step.dataset.typesLabel || 'PNG or JPG'} image.`;
        } else if (file.size > maxMb * 1024 * 1024) {
          message = `That image is ${formatSize(file.size)} — the limit is ${maxMb} MB.`;
        }
        if (message) {
          input.value = '';
          if (error) {
            error.textContent = message;
            error.hidden = false;
          }
          this.showFile(null);
          this.render();
          return;
        }
      }

      this.showFile(file || null);
      this.state = 'idle';
      this.render();
    }

    showFile(file) {
      const step = this.customStep;
      const zone = step.querySelector('[data-ch-upload-zone]');
      const row = step.querySelector('[data-ch-upload-file]');
      const thumb = step.querySelector('[data-ch-upload-thumb]');
      const name = step.querySelector('[data-ch-upload-name]');
      const size = step.querySelector('[data-ch-upload-size]');
      const warn = step.querySelector('[data-ch-upload-warn]');
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      if (warn) warn.hidden = true;

      if (!file) {
        if (zone) zone.hidden = false;
        if (row) row.hidden = true;
        if (thumb) {
          thumb.hidden = true;
          thumb.removeAttribute('src');
        }
        return;
      }

      if (zone) zone.hidden = true;
      if (row) row.hidden = false;
      if (name) name.textContent = file.name;
      if (size) size.textContent = formatSize(file.size);
      if (!thumb) return;

      const minPx = parseInt(step.dataset.minPx, 10) || 0;
      thumb.hidden = true;
      thumb.onload = () => {
        thumb.hidden = false;
        const w = thumb.naturalWidth;
        const h = thumb.naturalHeight;
        if (!w || !h) return;
        if (size) size.textContent = `${formatSize(file.size)} · ${w}×${h}px`;
        if (warn && minPx && Math.max(w, h) < minPx) warn.hidden = false;
      };
      // Formats the browser can't preview (e.g. HEIC) keep the striped tile
      thumb.onerror = () => {
        thumb.hidden = true;
      };
      this.previewUrl = URL.createObjectURL(file);
      thumb.src = this.previewUrl;
    }

    removeFile() {
      const input = this.uploadInput;
      if (!input) return;
      input.value = '';
      const error = this.customStep.querySelector('[data-ch-upload-error]');
      if (error) error.hidden = true;
      this.showFile(null);
      this.render();
    }

    /* ---------- Delivery ---------- */

    initPin() {
      const input = this.$('[data-ch-pin]');
      if (!input) return;
      try {
        const saved = localStorage.getItem(PIN_KEY);
        if (saved) input.value = saved;
      } catch (e) {}
      input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 6);
        const result = this.$('[data-ch-pin-result]');
        if (result) result.hidden = true;
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.checkPin();
        }
      });
    }

    checkPin() {
      const box = this.$('[data-ch-delivery]');
      const input = this.$('[data-ch-pin]');
      const result = this.$('[data-ch-pin-result]');
      if (!box || !input || !result) return;
      const pin = input.value.trim();
      const list = (key) =>
        (box.dataset[key] || '')
          .split(/[\s,]+/)
          .map((p) => p.trim())
          .filter(Boolean);
      const valid = /^[1-9][0-9]{5}$/.test(pin);
      const blocked = list('blocked').some((p) => pin.startsWith(p));
      const ok = valid && !blocked;

      result.hidden = false;
      result.classList.toggle('is-fail', !ok);
      this.$('[data-ch-pin-mark]').textContent = ok ? '✓' : '×';

      if (ok) {
        try {
          localStorage.setItem(PIN_KEY, pin);
        } catch (e) {}
        const metro = list('metro').some((p) => pin.startsWith(p));
        const min = parseInt(metro ? box.dataset.metroMin : box.dataset.otherMin, 10) || 2;
        const max = parseInt(metro ? box.dataset.metroMax : box.dataset.otherMax, 10) || 6;
        const d1 = new Date();
        const d2 = new Date();
        d1.setDate(d1.getDate() + min);
        d2.setDate(d2.getDate() + max);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const mon = (d) => months[d.getMonth()];
        const range = d1.getMonth() === d2.getMonth() ? `${d1.getDate()}–${d2.getDate()} ${mon(d2)}` : `${d1.getDate()} ${mon(d1)} – ${d2.getDate()} ${mon(d2)}`;
        this.$('[data-ch-pin-title]').textContent = box.dataset.okTitle.replace('[pincode]', pin);
        this.$('[data-ch-pin-text]').textContent = box.dataset.okText.replace('[dates]', range);
      } else {
        this.$('[data-ch-pin-title]').textContent = box.dataset.failTitle;
        this.$('[data-ch-pin-text]').textContent = box.dataset.failText;
      }
    }

    /* ---------- Sticky bar ---------- */

    initSticky() {
      const bar = this.$('[data-ch-sticky]');
      if (!bar) return;
      bar.hidden = false;
      const footer = document.querySelector('.ch-footer');
      if (footer && 'IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => bar.classList.toggle('is-away', entry.isIntersecting));
        }).observe(footer);
      }
    }

    /* ---------- Events ---------- */

    onClick(event) {
      const t = event.target;

      const thumb = t.closest('[data-ch-thumb]');
      if (thumb) {
        this.showMedia(parseInt(thumb.dataset.chThumb, 10));
        return;
      }

      const tier = t.closest('[data-ch-tier-qty]');
      if (tier) {
        this.qty = parseInt(tier.dataset.chTierQty, 10) || 1;
        this.$$('[data-ch-tier-qty]').forEach((b) => {
          const on = b === tier;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        this.render();
        return;
      }

      if (t.closest('[data-ch-upload-remove]')) {
        this.removeFile();
        return;
      }

      if (t.closest('[data-ch-pin-check]')) {
        this.checkPin();
        return;
      }

      if (t.closest('[data-ch-buy-now]')) {
        this.add({ buyNow: true });
        return;
      }

      if (t.closest('[data-ch-sticky-add]')) {
        this.add({});
      }
    }

    onChange(event) {
      const t = event.target;

      if (t.matches('[data-ch-brand]')) {
        this.fillModels('');
        this.savePhone();
        this.state = 'idle';
        this.render();
        return;
      }

      if (t.matches('[data-ch-model]')) {
        this.savePhone();
        this.state = 'idle';
        this.render();
        return;
      }

      if (t.matches('[data-ch-option]')) {
        const values = this.$$('[data-ch-option]').map((s) => s.value);
        const match = this.data.variants.find((v) => v.options.every((o, i) => o === values[i]));
        this.$$('[data-ch-option]').forEach((s, i) => {
          const note = this.$(`[data-ch-option-note="${i}"]`);
          if (note) note.textContent = s.value;
        });
        if (match) {
          this.variant = match;
          const input = this.$('[data-ch-variant-id]');
          if (input) input.value = match.id;
          if (match.media) {
            const idx = this.data.media.indexOf(match.media);
            if (idx > -1) this.showMedia(idx);
          }
          const url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url.toString());
        } else {
          this.variant = null;
        }
        this.render();
      }
    }

    onSubmit(event) {
      event.preventDefault();
      this.add({});
    }

    /* ---------- Add to cart ---------- */

    async add({ buyNow = false }) {
      if (this.state === 'loading') return;
      if (!this.variant || !this.variant.available) return;
      if (!this.phoneReady) {
        toast('Select your phone first');
        this.shake(this.phoneStep);
        return;
      }
      if (!this.designReady) {
        const step = this.customStep;
        toast(`${step.dataset.buttonLabel || 'Upload your design'} first`);
        this.shake(step);
        return;
      }

      this.state = 'loading';
      this.render();

      // With an image attached, the form is sent as multipart so Shopify can store the file
      if (this.hasFile) {
        try {
          const formData = new FormData(this.form);
          [...formData.entries()].forEach(([key, value]) => {
            if (value === '') formData.delete(key);
          });
          formData.set('id', this.variant.id);
          formData.set('quantity', this.qty);
          if (buyNow) {
            await window.Chloia.Cart.addForm(formData, { silent: true });
            window.location.href = '/checkout';
            return;
          }
          await window.Chloia.Cart.addForm(formData, { title: this.data.title });
          this.done();
        } catch (e) {
          this.state = 'idle';
          this.render();
          toast(e.message || 'Upload failed — please try again');
        }
        return;
      }

      const properties = {};
      this.$$('[data-ch-product-form] select[name^="properties["], [data-ch-product-form] input[name^="properties["]:not([type="file"]), [data-ch-product-form] textarea[name^="properties["]').forEach((el) => {
        const key = el.name.slice(11, -1);
        if (el.value) properties[key] = el.value;
      });

      try {
        if (buyNow) {
          await fetch(`${window.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: this.variant.id, quantity: this.qty, properties }] }),
          }).then((r) => {
            if (!r.ok) throw new Error('Could not add');
          });
          window.location.href = '/checkout';
          return;
        }
        await window.Chloia.Cart.add([{ id: this.variant.id, quantity: this.qty, properties }], { title: this.data.title });
        this.done();
      } catch (e) {
        this.state = 'idle';
        this.render();
        toast(e.message || 'Something went wrong');
      }
    }

    done() {
      this.state = 'done';
      this.render();
      setTimeout(() => {
        this.state = 'idle';
        this.render();
      }, 1500);
    }

    /* ---------- Render ---------- */

    render() {
      const v = this.variant;
      const ready = this.ready;

      // Price block
      if (v) {
        const now = this.$('[data-ch-price-now]');
        const was = this.$('[data-ch-price-was]');
        const off = this.$('[data-ch-price-off]');
        if (now) now.textContent = money(v.price);
        if (was) {
          was.hidden = !(v.compare > v.price);
          was.textContent = money(v.compare);
        }
        if (off) {
          const pct = v.compare > v.price ? Math.round(((v.compare - v.price) / v.compare) * 100) : 0;
          off.hidden = pct === 0;
          off.textContent = `${pct}% ${off.dataset.suffix || ''}`.trim();
        }
      }

      // Tier prices follow the selected variant
      if (v) {
        this.$$('[data-ch-tier-qty]').forEach((btn) => {
          const q = parseInt(btn.dataset.chTierQty, 10);
          const total = this.tierTotal(q);
          const save = v.price * q - total;
          const price = btn.querySelector('[data-ch-tier-price]');
          const saveEl = btn.querySelector('[data-ch-tier-save]');
          if (price) price.textContent = money(total);
          if (saveEl) {
            saveEl.textContent = save > 0 ? `${saveEl.dataset.saveLabel} ${money(save)}` : saveEl.dataset.standardLabel;
            saveEl.classList.toggle('is-saving', save > 0);
          }
        });
      }

      // Phone status + model select state
      const status = this.$('[data-ch-phone-status]');
      const modelSelect = this.$('[data-ch-model]');
      if (status) {
        status.textContent = this.model || status.dataset.requiredLabel;
        status.classList.toggle('is-required', !this.model);
      }
      if (modelSelect) modelSelect.classList.toggle('is-ready', !!this.model);

      // Design upload status
      const custom = this.customStep;
      const customStatus = custom && custom.querySelector('[data-ch-custom-status]');
      if (customStatus) {
        const required = custom.getAttribute('data-required') === 'true';
        customStatus.textContent = this.hasFile ? customStatus.dataset.addedLabel : required ? customStatus.dataset.requiredLabel : '';
        customStatus.classList.toggle('is-required', required && !this.hasFile);
      }

      // Quantity input
      const qtyInput = this.$('[data-ch-qty]');
      if (qtyInput) qtyInput.value = this.qty;

      // Buttons
      const add = this.$('[data-ch-add]');
      const buy = this.$('[data-ch-buy-now]');
      const sticky = this.$('[data-ch-sticky-add]');
      const labels = add ? add.dataset : {};
      const total = v ? this.tierTotal(this.qty) : 0;
      let label;
      let cls = '';
      if (!v || !v.available) {
        label = labels.labelSoldout || 'Sold out';
        cls = 'is-soldout';
      } else if (this.state === 'loading') {
        label = this.hasFile && custom ? custom.dataset.uploadingLabel || 'Uploading…' : labels.labelAdding || 'Adding…';
      } else if (this.state === 'done') {
        label = labels.labelAdded || 'Added ✓';
        cls = 'is-done';
      } else if (!ready) {
        label = !this.phoneReady ? labels.labelSelect || 'Select your phone' : (custom && custom.dataset.buttonLabel) || 'Upload your design';
        cls = 'is-waiting';
      } else {
        label = `${labels.labelAdd || 'Add to cart'} · ${money(total)}`;
      }

      [add, sticky].forEach((btn) => {
        if (!btn) return;
        btn.textContent = label;
        btn.classList.remove('is-soldout', 'is-done', 'is-waiting');
        if (cls) btn.classList.add(cls);
        btn.setAttribute('aria-disabled', !v || !v.available || !ready ? 'true' : 'false');
      });
      if (buy) buy.classList.toggle('is-waiting', !ready || !v || !v.available);

      // Sticky info
      const stickyPrice = this.$('[data-ch-sticky-price]');
      const stickyNote = this.$('[data-ch-sticky-note]');
      if (stickyPrice && v) stickyPrice.textContent = money(total);
      if (stickyNote) {
        const finish = this.$('[data-ch-finish]');
        const parts = [];
        if (this.model) parts.push(this.model);
        if (finish && finish.value) parts.push(finish.value);
        if (this.hasFile && custom) parts.push(custom.querySelector('.ch-step__title').textContent.trim().toLowerCase().replace(/^./, (c) => c.toUpperCase()));
        if (!this.phoneStep && v && v.title !== 'Default Title') parts.push(v.title);
        stickyNote.textContent = !ready ? label : parts.join(' · ');
      }
    }
  }

  customElements.define('ch-product', ChProduct);
})();
